import { useInfiniteQuery } from "@tanstack/react-query";
import { Toast } from "@newsela/angelou";
import api, { ApiCatalog } from "@/api";
import { logger } from "@/monitoring/utils";
import globalToasterRef from "@/base/globalToasterRef";

export type ISearchResults = typeof ApiCatalog.search.mockData;
export type ISearchInterpretResults =
  typeof ApiCatalog.searchInterpret.mockData;

interface UseInfinateSearchQueryParams {
  urlParams: string | URLSearchParams;
}

type ISearchResponse = ISearchResults | ISearchInterpretResults;

const SEARCH_LUNA_KEY = "isSearchWithLunaEnabled";

const EVENT_TOAST_REQUEST_ERROR = {
  showIcon: true,
  statusColor: Toast.statusColor.danger,
  children:
    "Something went wrong. Please try again or switch to default search.",
};

export const useInfiniteSearchQuery = ({
  urlParams,
}: UseInfinateSearchQueryParams) => {
  const sanitizedParams = new URLSearchParams(urlParams);
  // Remove page since the infinite query handles it seperately.
  sanitizedParams.delete("page");

  const isSearchWithLunaEnabled =
    globalThis.window?.localStorage.getItem(SEARCH_LUNA_KEY);
  const isInterpret =
    isSearchWithLunaEnabled !== null && isSearchWithLunaEnabled === "true";

  const queryKey = isInterpret
    ? [ApiCatalog.searchInterpret.key, sanitizedParams.toString()]
    : [ApiCatalog.search.key, sanitizedParams.toString()];

  return useInfiniteQuery({
    initialPageParam: 0,
    getNextPageParam: (lastPage: ISearchResponse, allPages) => {
      // Determine next page based on how many total results
      // and how many we current have loaded.
      const currentResultsCount = allPages.reduce(
        (acc, page) => acc + page.results.length,
        0,
      );
      return currentResultsCount < lastPage.total_result_count
        ? allPages.length
        : undefined;
    },
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      const prompt = sanitizedParams.get("prompt");
      if (isInterpret && prompt) {
        try {
          const interpretResponse = (await api().post(
            `${ApiCatalog.searchInterpret.endpoint}?page=${pageParam}`,
            { prompt },
          )) as ISearchInterpretResults;
          if (interpretResponse.seti_failed) {
            throw new Error(`Delivery search interpret empty chips`);
          }
          return interpretResponse;
        } catch (error: any) {
          logger.error(`Delivery search interpret failed: ${error}`);
          globalToasterRef.current.displayToast(EVENT_TOAST_REQUEST_ERROR);
          throw error;
        }
      }

      return await api().get(
        `${ApiCatalog.search.endpoint}?page=${pageParam}&${sanitizedParams}`,
      );
    },
  });
};
